import { useState } from "react";
import Modal from "@/components/base/Modal";
import { useToast } from "@/components/feature/ToastProvider";
import { writeTreat } from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
  /** 성공 시 생성된 번개 번호 전달 */
  onCreated: (no?: number) => void;
}

const MIN_PRESETS = [
  { label: "30분", value: 30 },
  { label: "50분", value: 50 },
  { label: "1시간", value: 60 },
  { label: "2시간", value: 120 },
];

// «내가 쏜다» 등록 모달 — 입력칸 최소 6개
export default function TreatWriteModal({ open, onClose, onCreated }: Props) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [place, setPlace] = useState("");
  const [meetTime, setMeetTime] = useState("12:30");
  const [limitCnt, setLimitCnt] = useState(5);
  const [limitMin, setLimitMin] = useState(50);
  const [customMin, setCustomMin] = useState(false);
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputClass =
    "w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

  const reset = () => {
    setSubject("");
    setPlace("");
    setMeetTime("12:30");
    setLimitCnt(5);
    setLimitMin(50);
    setCustomMin(false);
    setMemo("");
  };

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    const s = subject.trim();
    const p = place.trim();
    if (!s) {
      toast("제목을 입력해 주세요.", "info");
      return;
    }
    if (!p) {
      toast("장소를 입력해 주세요.", "info");
      return;
    }
    const min = Number(limitMin);
    if (!Number.isFinite(min) || min < 5 || min > 720) {
      toast("폭파시간은 5분 ~ 720분 사이로 정해 주세요.", "info");
      return;
    }
    setSubmitting(true);
    try {
      const res = await writeTreat({
        subject: s,
        place: p,
        meet_time: meetTime,
        limit_cnt: Math.max(0, Number(limitCnt) || 0),
        limit_min: min,
        memo: memo.trim(),
      });
      toast(res.msg || "번개를 올렸습니다!", "success");
      reset();
      onCreated(res.data?.no);
    } catch (e) {
      toast(e instanceof Error ? e.message : "등록에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} maxWidth="max-w-md" labelledBy="treat-write-title">
      <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
        <h2
          id="treat-write-title"
          className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950"
        >
          <span aria-hidden="true">🍚</span>
          내가 쏜다
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="닫기"
          className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
        >
          <i className="ri-close-line text-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div className="space-y-4 px-5 py-5">
        <div>
          <label htmlFor="treat-subject" className="mb-1.5 block text-sm font-semibold text-foreground-700">
            제목 <span className="text-accent-600">*</span>
          </label>
          <input
            id="treat-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={80}
            placeholder="예) 오늘 점심은 내가 쏜다. 선착순 5명"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="treat-place" className="mb-1.5 block text-sm font-semibold text-foreground-700">
            장소 <span className="text-accent-600">*</span>
          </label>
          <input
            id="treat-place"
            type="text"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            maxLength={80}
            placeholder="예) 선화동 한영식당"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <label htmlFor="treat-time" className="mb-1.5 block text-sm font-semibold text-foreground-700">
              만나는 시각
            </label>
            <input
              id="treat-time"
              type="time"
              value={meetTime}
              onChange={(e) => setMeetTime(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="treat-count" className="mb-1.5 block text-sm font-semibold text-foreground-700">
              선착순 인원
            </label>
            <input
              id="treat-count"
              type="number"
              min={0}
              max={99}
              value={limitCnt}
              onChange={(e) => setLimitCnt(Number(e.target.value))}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-foreground-400">0 이면 인원 제한 없음</p>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">폭파시간</p>
          <div className="flex flex-wrap gap-2">
            {MIN_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => {
                  setLimitMin(preset.value);
                  setCustomMin(false);
                }}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  !customMin && limitMin === preset.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCustomMin(true)}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                customMin
                  ? "border-primary-400 bg-primary-50 text-primary-700"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
              }`}
            >
              직접입력
            </button>
          </div>
          {customMin && (
            <div className="mt-2.5 flex items-center gap-2">
              <input
                type="number"
                min={5}
                max={720}
                value={limitMin}
                onChange={(e) => setLimitMin(Number(e.target.value))}
                className={`${inputClass} w-28`}
                aria-label="폭파시간(분)"
              />
              <span className="text-sm text-foreground-500">분 (5 ~ 720)</span>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="treat-memo" className="mb-1.5 block text-sm font-semibold text-foreground-700">
            한 줄 메모 (선택)
          </label>
          <input
            id="treat-memo"
            type="text"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            maxLength={120}
            placeholder="예) 늦게 오셔도 됩니다"
            className={inputClass}
          />
        </div>

        <p className="rounded-lg bg-background-100 px-3 py-2 text-xs text-foreground-500">
          날짜는 오늘로만 등록됩니다. 미리 예약은 안 됩니다(번개).
        </p>
      </div>

      <div className="flex gap-2 border-t border-background-200 px-5 py-4">
        <button
          type="button"
          onClick={close}
          disabled={submitting}
          className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
        >
          취소
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          {submitting ? "올리는 중…" : "번개 올리기"}
        </button>
      </div>
    </Modal>
  );
}